import {createContext, useContext, useRef} from 'react'
import type {PropsWithChildren} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createLineSelectionSlice, type LineSelectionSlice} from './slices/line-selection'
import {
  createDraggingAddCommentButtonSlice,
  type DraggingAddCommentButtonSlice,
} from './slices/dragging-add-comment-button'
import {
  createInjectedContextLinesSlice,
  type InjectedContextLinesSlice,
  type InjectedContextlinesSliceOpts,
} from './slices/injected-context-lines'

type DiffStoreState = DraggingAddCommentButtonSlice & InjectedContextLinesSlice & LineSelectionSlice

type DiffStore = ReturnType<typeof createDiffStore>

type DiffStoreOpts = {
  pathDigest: string
} & InjectedContextlinesSliceOpts

const DiffStoreContext = createContext<DiffStore | null>(null)

export function useDiffStore<T>(selector: (state: DiffStoreState) => T): T {
  const store = useContext(DiffStoreContext)
  if (!store) throw new Error('Missing DiffStoreProvider in the tree')

  return useStore(store, selector)
}

export const DiffStoreProvider = ({children, ...opts}: PropsWithChildren<DiffStoreOpts>) => {
  const storeRef = useRef<DiffStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createDiffStore(opts)
  }

  return <DiffStoreContext.Provider value={storeRef.current}>{children}</DiffStoreContext.Provider>
}

const createDiffStore = ({pathDigest, addInjectedContextLines}: DiffStoreOpts) =>
  createStore<DiffStoreState>()(
    devtools(
      (...a) => ({
        ...createDraggingAddCommentButtonSlice()(...a),
        ...createInjectedContextLinesSlice({addInjectedContextLines})(...a),
        ...createLineSelectionSlice()(...a),
      }),
      {name: `diff-store-${pathDigest}`},
    ),
  )

try{ DiffStoreContext.displayName ||= 'DiffStoreContext' } catch {}
try{ DiffStoreProvider.displayName ||= 'DiffStoreProvider' } catch {}